<template>
  <el-form ref="formRef" :model="formState" :rules="rules">
    <el-row :gutter="16">
      <el-col :span="12">
        <el-form-item label="申请提款金额（元）" prop="drawingAmount">
          <RxkInput
            :maxLength="13"
            v-model="formState.drawingAmount"
            :formatter="moneyFormatter"
            :parser="moneyParser"
            :disabled="getDisabled(permissions, 'drawingAmount')"
          />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item label="贷款用途" prop="loanUse">
          <RxkInput
            :maxLength="20"
            v-model="formState.loanUse"
            :disabled="getDisabled(permissions, 'loanUse')"
          />
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="借款人" prop="borrowerName">
          <span>{{ info.borrowerName }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="手机号" prop="borrowerPhone">
          <span>{{ info.borrowerPhone }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="身份证" prop="borrowerIdCard">
          <span>{{ info.borrowerIdCard }}</span>
        </el-form-item>
      </el-col>

      <el-col :span="8">
        <el-form-item label="产品名称">
          <span>{{ info.productName }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="产品类型">
          <span>{{ productTypeText(info.productType) }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="还款方式" prop="repaymentType">
          <span>{{ typeText(info.repaymentType) }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="还款频率">
          <span>{{ rateText(info.repaymentFrequency) }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="贷款期限">
          <span>{{ info.loanDate }}月</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="贷款起始日期">
          <span>{{ formatToDateTime(info.creditBeginTime, 'YYYY-MM-DD') }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="贷款截止日期">
          <span>{{ formatToDateTime(info.creditEndTime, 'YYYY-MM-DD') }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="还款日">
          <span>{{ info.repaymentDate }}</span>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="贷款年利率">
          <span>{{ info.yearRate }}%</span>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>
<script lang="ts" setup>
import { RxkInput } from '@/components/common'
import { moneyExp, rateList, typeList, loanList } from '@/views/erp/enums'
import { getDisabled } from '@/components/business/FlowDeal/components/loanDetailsLayout/useSubTable'
import { formatToDateTime } from '@/utils/date'
import { cloneDeep } from '@/utils/tools'
import { moneyFormatter, moneyParser } from '@/utils/money'

interface FormState {
  [x: string]: any
  loanUse: string //	贷款用途	string
  drawingAmount: string // 提款金额
  creditAmount?: number // 授信金额
}

const props = defineProps({
  formValue: {
    type: Object as PropType<FormState>,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: () => ({})
  }
})

const formRef = ref()

const formState = reactive<FormState>({
  loanUse: '',
  drawingAmount: ''
})

function moneyValidator(_rules: any, value: string, callback: Fn) {
  let num = Number(value)
  let creditAmount = props.formValue.creditAmount
  if (isNaN(num)) {
    callback(new Error('请输入正确的金额'))
  } else if (creditAmount && num > creditAmount) {
    callback(new Error('不能大于授信金额！'))
  } else {
    callback()
  }
}

const defaultRules = {
  loanUse: [{ required: true, message: '请输入贷款用途', trigger: 'blur' }],
  drawingAmount: [
    { required: true, message: '请输入提款金额', trigger: 'blur' },
    {
      pattern: moneyExp,
      message: '请输入小数位2位以内的正确值',
      trigger: 'blur'
    },
    { validator: moneyValidator, trigger: 'blur' }
  ]
}

function rateText(value: number) {
  return rateList.find(item => item.value === value)?.label || ''
}

function typeText(value: number) {
  return typeList.find(item => item.value === value)?.label || ''
}

function productTypeText(value: number) {
  return loanList.find(item => item.value === value)?.label || ''
}

const rules = computed(() => {
  let drawingAmount = props?.permissions?.['drawingAmount']
  let loanUse = props?.permissions?.['loanUse']
  let rules: Recordable = {}
  if (drawingAmount instanceof Array && drawingAmount.includes('REQUIRED')) {
    rules.drawingAmount = defaultRules.drawingAmount
  }
  if (loanUse instanceof Array && loanUse.includes('REQUIRED')) {
    rules.loanUse = defaultRules.loanUse
  }
  return rules
})

const info = computed(() => {
  return cloneDeep(props.formValue)
})

onMounted(() => {
  for (let key in formState) {
    formState[key] = props.formValue[key]
  }
})

async function sure() {
  let valid = false
  try {
    valid = await formRef.value.validate()
  } catch (e) {
    console.log(e)
  }
  if (valid) {
    return { ...formState }
  }
  return false
}

defineExpose({
  sure
})
</script>
